// TelcoTrack — Editovatelný snapshot účastníků / parcel na akviziční smlouvě.
// KN generate jednou předvyplní; další regenerace bere snapshot, dokud uživatel nerestartuje z KN.

import { parseR100LengthM } from '../utils/r100-parcel-length.util';
import { normalizeContractPartyNpValue } from './object-workflow/digitalni-cesta-spec.constants';

/** Typ subjektu (parita KN CHAROS → subjekt_typ). */
export type ContractDocumentPartySubjektTyp =
  | 'fo'
  | 'sjm'
  | 'firma'
  | 'obec'
  | 'stat'
  | 'jine'
  | string;

/**
 * Stav digitální cesty u BJ / obyvatele (per owner ve snapshotu).
 * Propisuje se do BJ řádků protokolu jako „souhlasí“ / „nesouhlasí“.
 */
export const DIGITALNI_CESTA_BJ_STAVY = ['schvalena', 'odmitnuta'] as const;
export type DigitalniCestaBjStav = (typeof DIGITALNI_CESTA_BJ_STAVY)[number];

export function normalizeDigitalniCestaBjStav(
  raw: unknown,
): DigitalniCestaBjStav | null {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  return (DIGITALNI_CESTA_BJ_STAVY as readonly string[]).includes(s)
    ? (s as DigitalniCestaBjStav)
    : null;
}

/** České UI labely tlačítek Schválena / Odmítnuta. */
export function digitalniCestaBjStavLabelCs(stav: DigitalniCestaBjStav): string {
  return stav === 'schvalena' ? 'Schválena' : 'Odmítnuta';
}

/**
 * Text do BJ řádku protokolu. `null` = nechat fallback (podpis / „souhlasí / nesouhlasí").
 */
export function digitalniCestaBjStavDocumentMark(
  stav: DigitalniCestaBjStav | null | undefined,
): string | null {
  if (stav === 'schvalena') return 'souhlasí';
  if (stav === 'odmitnuta') return 'nesouhlasí';
  return null;
}

/**
 * Před podpisem protokolu digitální cesty: každá osoba musí mít Schválena/Odmítnuta.
 * Počet cest ve spec je nepovinný (billing) — neblokuje podpis.
 * `digitalniCestaCount` zůstává v opts kvůli call-site kompatibilitě.
 */
export function digitalniCestaSignBlockedReason(opts: {
  owners: ReadonlyArray<{ digitalniCestaStav?: DigitalniCestaBjStav | null } | null | undefined>;
  digitalniCestaCount?: number | null | undefined;
}): string | null {
  void opts.digitalniCestaCount;
  const owners = (opts.owners ?? []).filter((o): o is NonNullable<typeof o> => o != null);
  if (owners.length === 0) {
    return 'Doplňte údaje vlastníků před podpisem.';
  }
  for (const o of owners) {
    if (!normalizeDigitalniCestaBjStav(o.digitalniCestaStav)) {
      return 'U každé osoby nastavte Schválena nebo Odmítnuta.';
    }
  }
  return null;
}

/** Vlastník / oprávněná osoba ve snapshotu smlouvy (editovatelný). */
export interface ContractDocumentPartyOwner {
  /** Stabilní id pro UI (volitelné; při uložení se doplní). */
  id?: string | null;
  jmenoNazev: string;
  subjektTyp?: ContractDocumentPartySubjektTyp | null;
  podil?: string | null;
  adresa?: string | null;
  ico?: string | null;
  dic?: string | null;
  spisovaZnacka?: string | null;
  /** Zástupci k podpisu (jméno podepisující osoby; default = jméno vlastníka). */
  zastupci?: string | null;
  bankovniUcet?: string | null;
  datumNarozeni?: string | null;
  email?: string | null;
  telefon?: string | null;
  /** Nadzemní podlaží — protokol digitální cesty. */
  np?: string | null;
  /**
   * Schválení / odmítnutí digitální cesty u tohoto BJ (jen `digitalni_cesta`).
   * Persistuje se v JSONB `document_parties` (bez samostatné migrace).
   */
  digitalniCestaStav?: DigitalniCestaBjStav | null;
  /**
   * Volitelná poznámka k schválení / odmítnutí digitální cesty (jen `digitalni_cesta`).
   * Propisuje se do BJ řádku protokolu.
   */
  digitalniCestaPoznamka?: string | null;
}

/** Parcela / identifikace nemovitosti ve snapshotu smlouvy. */
export interface ContractDocumentPartyParcel {
  id?: string | null;
  kuNazev?: string | null;
  obec?: string | null;
  /** Parcelní číslo / label (např. „3973/3“, „St. 82“). */
  parcelniCislo: string;
  lv?: string | null;
  vymera?: number | null;
  /** Délka vrstvy R100 na parcele (běžné metry). null = neznámé. */
  r100LengthM?: number | null;
  druhPozemku?: string | null;
  ulice?: string | null;
  popisneCislo?: string | null;
  orientacniCislo?: string | null;
}

/** JSONB `acquisition_contracts.document_parties`. */
export interface ContractDocumentParties {
  owners: ContractDocumentPartyOwner[];
  parcels: ContractDocumentPartyParcel[];
  /** ISO čas posledního KN předvyplnění. */
  knPrefillAt?: string | null;
  /** Uživatel změnil snapshot od posledního KN prefill. */
  userEdited?: boolean;
}

export function hasContractDocumentParties(
  parties: ContractDocumentParties | null | undefined,
): parties is ContractDocumentParties {
  if (!parties || typeof parties !== 'object') return false;
  return Array.isArray(parties.owners) && Array.isArray(parties.parcels);
}

/** Normalizace / sanitizace payloadu před uložením (API + generate). */
export function normalizeContractDocumentParties(
  input: ContractDocumentParties | null | undefined,
  opts?: { userEdited?: boolean; knPrefillAt?: string | null; assignIds?: boolean },
): ContractDocumentParties {
  const ownersIn = Array.isArray(input?.owners) ? input!.owners : [];
  const parcelsIn = Array.isArray(input?.parcels) ? input!.parcels : [];
  const assignIds = opts?.assignIds !== false;

  const owners: ContractDocumentPartyOwner[] = ownersIn.map((raw, index) => {
    const o = raw && typeof raw === 'object' ? raw : { jmenoNazev: '' };
    const id =
      (typeof o.id === 'string' && o.id.trim()) ||
      (assignIds ? `owner-${index + 1}` : undefined);
    return {
      id: id ?? null,
      jmenoNazev: String(o.jmenoNazev ?? '').trim(),
      subjektTyp: (o.subjektTyp ?? 'fo') as ContractDocumentPartySubjektTyp,
      podil: trimOrNull(o.podil),
      adresa: trimOrNull(o.adresa),
      ico: trimOrNull(o.ico),
      dic: trimOrNull(o.dic),
      spisovaZnacka: trimOrNull(o.spisovaZnacka),
      zastupci: trimOrNull(o.zastupci),
      bankovniUcet: trimOrNull(o.bankovniUcet),
      datumNarozeni: trimOrNull(o.datumNarozeni),
      email: trimOrNull(o.email),
      telefon: trimOrNull(o.telefon),
      // Digits-only floor ordinal — bare „pp“ / „NP“ → null (parity protocol field).
      np: normalizeContractPartyNpValue(o.np),
      digitalniCestaStav: normalizeDigitalniCestaBjStav(
        (o as ContractDocumentPartyOwner).digitalniCestaStav,
      ),
      digitalniCestaPoznamka: trimOrNull(
        (o as ContractDocumentPartyOwner).digitalniCestaPoznamka,
      ),
    };
  });

  const parcels: ContractDocumentPartyParcel[] = parcelsIn.map((raw, index) => {
    const p = raw && typeof raw === 'object' ? raw : { parcelniCislo: '' };
    const id =
      (typeof p.id === 'string' && p.id.trim()) ||
      (assignIds ? `parcel-${index + 1}` : undefined);
    const vymeraRaw = p.vymera;
    const vymera =
      vymeraRaw == null || vymeraRaw === ('' as unknown)
        ? null
        : Number.isFinite(Number(vymeraRaw))
          ? Number(vymeraRaw)
          : null;
    const r100LengthM = parseR100LengthM(p.r100LengthM);
    return {
      id: id ?? null,
      kuNazev: trimOrNull(p.kuNazev),
      obec: trimOrNull(p.obec),
      parcelniCislo: String(p.parcelniCislo ?? '').trim(),
      lv: trimOrNull(p.lv),
      vymera,
      r100LengthM,
      druhPozemku: trimOrNull(p.druhPozemku),
      ulice: trimOrNull(p.ulice),
      popisneCislo: trimOrNull(p.popisneCislo),
      orientacniCislo: trimOrNull(p.orientacniCislo),
    };
  });

  return {
    owners,
    parcels,
    knPrefillAt:
      opts?.knPrefillAt !== undefined
        ? opts.knPrefillAt
        : (input?.knPrefillAt ?? null),
    userEdited:
      opts?.userEdited !== undefined ? opts.userEdited : !!input?.userEdited,
  };
}

function trimOrNull(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

/**
 * Podíl do snapshotu / šablony smlouvy z KN.
 * KN často vrací prázdný `<PODIL/>` u výlučného vlastnictví — do smlouvy dáme `1/1`
 * jen když je na smlouvě **jeden** vlastník. U více vlastníků bez podílu nevymýšlet
 * (parita trial dual-owner, aae0e3c9). Neměnit `acquisition_parcel_owners` /
 * `owners_signature` — jen document_parties / template / list DTO.
 */
export function resolveContractPodilFromKn(
  podil: string | null | undefined,
  ownerCount: number,
): string | null {
  const trimmed = (podil ?? '').trim();
  if (trimmed) return trimmed;
  if (ownerCount === 1) return '1/1';
  return null;
}

/**
 * Doplní `1/1` u výlučného vlastnictví (sole owner + prázdný podíl) ve snapshotu.
 * Vrací `true`, pokud došlo ke změně. U `userEdited` nic nemění.
 */
export function upgradeExclusiveOwnershipPodilInParties(
  parties: ContractDocumentParties,
): { parties: ContractDocumentParties; upgraded: boolean } {
  if (parties.userEdited) return { parties, upgraded: false };
  const owners = parties.owners ?? [];
  if (owners.length !== 1) return { parties, upgraded: false };
  if ((owners[0].podil ?? '').trim()) return { parties, upgraded: false };
  const next = normalizeContractDocumentParties(
    {
      ...parties,
      owners: [{ ...owners[0], podil: '1/1' }],
    },
    { userEdited: false, knPrefillAt: parties.knPrefillAt ?? null },
  );
  return { parties: next, upgraded: true };
}
